import React, { useCallback, useEffect, useMemo, useState } from 'react';
import request from '../../../../helpers/request';
import endpoints from '../../../../helpers/endpoints';
import syncRowHeights from '../../../../helpers/syncRowHeights';
import { GenericTable } from '../GenericTable';
import { PostcodeSearchModal } from '../modals/PostcodeSearchModal';
import { DuplicateWarningModal } from '../modals/DuplicateWarningModal';
import classCodes from '../../../../assets/classification-codes.json';

export const AddressBase = ({ translations, summaryData: data, selectedMatches, setSelectedMatches, prefix, disabledByUserType, setUserInput }) => {
  const [loading, setLoading] = useState(false);
  const [addressData, setAddressData] = useState({});
  const [filteringModalIsOpen, setFilteringModalIsOpen] = useState(false);
  const [postcodeSearch, setPostcodeSearch] = useState('');
  const [nameSearch, setNameSearch] = useState('');
  const [index, setIndex] = useState(0);
  const [duplicateWarningModal, setDuplicateWarningModal] = useState(false)
  const [duplicateWarningAccepted, setDuplicateWarningAccepted] = useState(false)

  const cycleIndex = useCallback((change = 0) => {
    if (loading || change === 0 ) return
    const totalIndex = (addressData.per_page * (addressData.current_page - 1)) + index
    if (totalIndex + change <= -1) return
    if (totalIndex + change >= ( addressData.total )) return
    if (totalIndex + change > addressData.to - 1 || totalIndex + change < addressData.from - 1) {
      setLoading(true);
      getData(null, change > 0 ? addressData.current_page + 1 : addressData.current_page - 1)
    }
    setIndex(index + change)
  }, [index, addressData])

  const getData = (customInput, page) => {
    if (!customInput && !postcodeSearch && (!data.Matched_UPRN_Postcode || data.Address_Status === 'Invalid Postcode') && !data.CMOS_Postcode) {
        setLoading(false);
        return;
    }
    setIndex(0)
    const oldData = addressData ?
        {...addressData, current_page: page ? addressData.current_page : 0}
      : 
        {}
    request(true)
      .get(endpoints[`${prefix}_GET_ADDRESSES`], {
        doesCancel: true,
        params: {
          abp_reference: data.CF_UPRN || data.Matched_UPRN || data.CMOS_UPRN,
          postcode: customInput || postcodeSearch || data.Matched_UPRN_Postcode || data.CMOS_Postcode || data.CMOS_Meter_Address_Postcode,
          spid: data.Core_SPID,
          page: page,
          name_search: nameSearch
        }
      })
      .then(e => {
        const abpData = e.data?.data ?? []
        abpData.forEach(datum => {
          if (datum.CHANGE_CODE) {
            datum.CHANGE_CODE = datum.CHANGE_CODE === "D" ? "Deleted" : "Active"
          }
          if (datum.CLASSIFICATION_CODE) {
            datum.CLASSIFICATION_CODE = classCodes.find(classCode => classCode.Concatenated === datum?.CLASSIFICATION_CODE)?.Class_Desc
          }
        })
        setAddressData(e.data);
        var displayFirst = -1;
        if ((data.CF_UPRN || data.Matched_UPRN || data.CMOS_UPRN) && !oldData.current_page) {
          displayFirst = abpData.findIndex(_ => _.UPRN === parseInt([data.CF_UPRN, data.Matched_UPRN, data.CMOS_UPRN].find(value => value)?.trim()));
          setSelectedMatches((prevMatches) => {
            return {
              ...prevMatches,
              abp: abpData[displayFirst]
            };
          });
        }
        const startIndex = oldData.current_page > e.data.current_page ? e.data.per_page - 1 : 0
        const newIndex = displayFirst !== -1 ? displayFirst : startIndex
        setIndex(newIndex);
        setLoading(false)
      })
      .catch(_ => {
        console.log(_)
        window.alert('Could not get Address Base data.');
        setAddressData([]);
        setLoading(false) 
      });
  };

  useEffect(() => {
    if (syncRowHeights) {
      syncRowHeights()
    }
    setDuplicateWarningAccepted(false)
  }, [addressData, index])

  useEffect(() => {
    if (duplicateWarningAccepted === true) {
      selectMatch()
    }
  }, [duplicateWarningAccepted])

  useEffect(() => {
    setAddressData([])
    setIndex(-1)
    if (data.Core_SPID) {
      setIndex(-1)
      setPostcodeSearch('')
      setNameSearch('')
      setLoading(true)
      getData(data.Matched_UPRN_Postcode || data.CMOS_Postcode)
    } else {
      setLoading(false)
    }
  }, [data.Core_SPID]);

  const selectMatch = useCallback(() => {
    if (!addressData.data?.[index]?.UPRN) return
    if (addressData.data?.[index]?.Duplication_Status && duplicateWarningAccepted !== true) {
      setDuplicateWarningModal(true)
      return
    }
    setSelectedMatches((prevMatches) => ({
      ...prevMatches,
       abp: addressData.data?.[index]
    }))
  }, [addressData, selectedMatches, setSelectedMatches, index, duplicateWarningAccepted])

  const updateUPRN = () => {
    setUserInput(prevUserInput => ({
      ...prevUserInput,
      CF_UPRN: addressData.data?.[index]?.UPRN,
      CF_UPRN_Reason_Code: ''
    }))
  }

  const restorePostcode = useCallback(() => {
    setIndex(-1)
    setAddressData({});
    setPostcodeSearch(null); 
    setNameSearch(null);
  }, [postcodeSearch, nameSearch])

  useEffect(() => {
    if (postcodeSearch === null && nameSearch === null) {
      setPostcodeSearch(''); 
      setNameSearch('');
      getData();
    }
  }, [postcodeSearch, nameSearch])

  const rows = useMemo(() => {
    return [
      [translations['UPRN'], 'UPRN'],
      [translations['STATUS'], 'CHANGE_CODE'],
      [translations['CLASSIFICATION_CODE'], 'CLASSIFICATION_CODE'],
      [translations['GISX'], 'EASTING'],
      [translations['GISY'], 'NORTHING'],
      [translations['ORGANISATION'], 'ORGANISATION'],
      [translations['SUB_BUILDING'], 'SUB_BUILDING'],
      [translations['BUILDING_NAME'], 'BUILDING_NAME'],
      [translations['BUILDING_NUMBER'], 'BUILDING_NUMBER'],
      [translations['STREET_NAME'], 'STREET_NAME'],
      [translations['LOCALITY'], 'LOCALITY'],
      [translations['TOWN_NAME'], 'TOWN_NAME'],
      [translations['POST_TOWN'], 'POST_TOWN'],
      [translations['Postcode'], 'POSTCODE'],
      ['SINGLE_LINE_ADDRESS'],
      [
        () => <button disabled={disabledByUserType} className={`button compact smaller-text ${!disabledByUserType && addressData.data?.[index] ? 'background-primary colour-white' : ''}`} onClick={() => updateUPRN()}>Update UPRN Reference</button>,
        () => <button disabled={disabledByUserType} className={`button compact smaller-text ${selectedMatches.abp && selectedMatches.abp.UPRN == addressData.data?.[index]?.UPRN && !disabledByUserType ? 'background-primary colour-white' : ''}`} onClick={() => selectMatch()}>Select Match</button>
      ]
    ]
  }, [translations, window.reloadSummaryData, index, selectedMatches, addressData])

  return [
    <GenericTable title={translations['ABP_Header']} rows={rows} data={addressData.data} paginationData={addressData} loading={loading} restorePostcode={restorePostcode} index={index} cycleIndex={cycleIndex} customPostcode={postcodeSearch || nameSearch} searchEnabled={true} setPostcodeModal={setFilteringModalIsOpen} />,
    <PostcodeSearchModal postcodeModal={filteringModalIsOpen} setPostcodeModal={setFilteringModalIsOpen} customPostcode={postcodeSearch} setCustomPostcode={setPostcodeSearch} nameSearch={nameSearch} setNameSearch={setNameSearch} setLoading={setLoading} getData={getData} />,
    <DuplicateWarningModal isOpen={duplicateWarningModal} setIsOpen={setDuplicateWarningModal} setAccepted={setDuplicateWarningAccepted} address={addressData.data?.[index]} />
  ];
};